import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import { deleteTopic } from "./deleteTopicSlice"
import { deletePostsBulk } from "./deletePostsBulkSlice"
import { ROLE_TAB_CONFIG, topicTabRole } from "../../lib/utils/roleTabConfig"
import {
  buildPostAnalyticsApiPath,
  buildPostAnalyticsTopicIdsQuery,
  normalizePostAnalyticsResponse,
} from "../../lib/utils/postAnalytics"

// Content post status constants
export const CONTENT_POST_STATUS = {
  DRAFT: 1,
  SENT_FOR_APPROVAL: 4,
  SENT_TO_CREATOR: 5,
  SCHEDULED: 6,
  POSTED: 7,
  SENT_FOR_EXTERNAL_APPROVAL: 8,
}

/** Resolve post status from nested or flat API fields (supports AI status 0). */
export function getPostStatusId(post) {
  if (post == null) return undefined
  const nested = post.contentPostStatus?.id
  if (nested != null && nested !== "") {
    const n = Number(nested)
    return Number.isNaN(n) ? undefined : n
  }
  const flat = post.contentPostStatusId
  if (flat != null && flat !== "") {
    const n = Number(flat)
    return Number.isNaN(n) ? undefined : n
  }
  return undefined
}

// Status mapping for UI
export const STATUS_MAPPING = {
  [CONTENT_POST_STATUS.DRAFT]: "drafts",
  [CONTENT_POST_STATUS.SENT_TO_CREATOR]: "drafts", // Now maps to drafts for creator view
  [CONTENT_POST_STATUS.SENT_FOR_APPROVAL]: "pending",
  [CONTENT_POST_STATUS.SENT_FOR_EXTERNAL_APPROVAL]: "pending",
  [CONTENT_POST_STATUS.SCHEDULED]: "scheduled",
  [CONTENT_POST_STATUS.POSTED]: "live",
}

const toLocalYmd = (date) => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

const getMonthDates = (date = new Date()) => {
  const year = date.getFullYear()
  const month = date.getMonth()

  const startDate = new Date(year, month, 1)
  const endDate = new Date(year, month + 1, 0)

  return {
    start_date: toLocalYmd(startDate),
    end_date: toLocalYmd(endDate),
  }
}

export function coerceTopicsMonthDate(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value
  if (typeof value === "string") {
    const key = value.trim().slice(0, 10)
    if (/^\d{4}-\d{2}-\d{2}$/.test(key)) {
      const [y, m, d] = key.split("-").map(Number)
      const date = new Date(y, m - 1, d)
      if (!Number.isNaN(date.getTime())) return date
    }
  }
  return null
}

/** First valid calendar/post date; today only if nothing else was provided. */
export function resolveMonthlyTopicsDate(...candidates) {
  for (const value of candidates) {
    const date = coerceTopicsMonthDate(value)
    if (date) return date
  }
  return new Date()
}

export function topicsMonthCacheKey(clientId, date) {
  const d = resolveMonthlyTopicsDate(date)
  return `${String(clientId ?? "")}|${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
}

function topicsFetchCacheKey({ clientId, date, isYearlyCall = false, startDate, endDate } = {}) {
  if (startDate && endDate) return `${String(clientId)}|${startDate}|${endDate}`
  const targetDate = date || new Date()
  if (isYearlyCall) return `${String(clientId)}|${targetDate.getFullYear()}`
  return topicsMonthCacheKey(clientId, targetDate)
}

/** GET topics/clients/{id} for the visible month only (not a full year). */
export function monthlyTopicsFetchPayload(clientId, postStatusIds, date, force = false) {
  return {
    clientId,
    postStatusIds,
    date: resolveMonthlyTopicsDate(date),
    isYearlyCall: false,
    force: Boolean(force),
  }
}

const getYearDates = (date = new Date()) => {
  const year = date.getFullYear()

  const startDate = new Date(year, 0, 1) // January 1st
  const endDate = new Date(year, 11, 31) // December 31st

  return {
    start_date: toLocalYmd(startDate),
    end_date: toLocalYmd(endDate),
  }
}

const getMonthKey = (date = new Date()) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
}
export const fetchTopicDetails = createAsyncThunk(
  "topics/fetchTopicDetails",
  async ({ clientId, topicId }, { rejectWithValue }) => {
    try {
      if (!topicId) {
        return rejectWithValue("No topic ID provided")
      }

      const apiPath = buildPostAnalyticsApiPath(clientId)
      if (!apiPath) {
        return rejectWithValue("Client ID is required")
      }

      const query = buildPostAnalyticsTopicIdsQuery([topicId])
      const data = await get(`${apiPath}?${query}`)
      return data
    } catch (error) {
      console.error("Fetch topic details error:", error)
      console.error("Error details:", { status: error.status, message: error.message })
      return rejectWithValue(error.message || "Failed to fetch topic details")
    }
  },
)
// Async thunk for fetching topics by client ID
export const fetchTopicsByClientId = createAsyncThunk(
  "topics/fetchByClientId",
  async ({ clientId, date, isYearlyCall = false }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const token = state.auth?.token

      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }

      if (!clientId) {
        throw new Error("Client ID is required")
      }

      const targetDate = date || new Date()
      const { start_date, end_date } = isYearlyCall ? getYearDates(targetDate) : getMonthDates(targetDate)
      const monthKey = getMonthKey(targetDate)

      if (!isYearlyCall) {
        const currentTopics = state.topics
        if (currentTopics.lastFetchedMonth === monthKey && currentTopics.topics.length > 0) {
          return {
            data: currentTopics.topics,
            fromCache: true,
            monthKey: monthKey,
          }
        }
      }

      const queryParams = new URLSearchParams({
        start_date,
        end_date,
      })

      const data = await get(`topics/creator/${clientId}?${queryParams.toString()}`)

      return {
        data: data,
        fromCache: false,
        monthKey: monthKey,
      }
    } catch (err) {
      console.error("Topics fetch error:", err)
      return rejectWithValue(err?.message || "Failed to fetch topics")
    }
  },
)

// Helper function to get tabs for a role
export const getTabsForRole = (role) => {
  return ROLE_TAB_CONFIG[topicTabRole(role)]?.tabs || []
}

// Helper function to get all status IDs for a role
export const getAllStatusIdsForRole = (role) => {
  const roleTabs = ROLE_TAB_CONFIG[topicTabRole(role)]?.tabs || []
  const allStatusIds = []
  roleTabs.forEach((tab) => {
    tab.statusIds.forEach((id) => {
      if (!allStatusIds.includes(id)) {
        allStatusIds.push(id)
      }
    })
  })
  return allStatusIds
}

// Helper function to get status IDs for a specific tab
export const getStatusIdsForTab = (role, tabKey) => {
  const roleConfig = ROLE_TAB_CONFIG[topicTabRole(role)]
  if (!roleConfig) return []

  const tab = roleConfig.tabs.find((t) => t.key === tabKey)
  return tab?.statusIds || []
}

// Unified async thunk for fetching topics with status filtering (for all roles)
export function flattenTopicsForClient(topicsByMonth, clientId) {
  const prefix = `${String(clientId)}|`
  const byId = new Map()
  Object.entries(topicsByMonth || {}).forEach(([key, rows]) => {
    if (!key.startsWith(prefix) || !Array.isArray(rows)) return
    rows.forEach((topic) => {
      const id = topic?.id
      if (id == null) {
        byId.set(`${key}|anon|${byId.size}`, topic)
        return
      }
      const sid = String(id)
      const existing = byId.get(sid)
      if (!existing) {
        byId.set(sid, topic)
        return
      }
      const existingPosts = Array.isArray(existing.contentTopicPosts) ? existing.contentTopicPosts : []
      const nextPosts = Array.isArray(topic.contentTopicPosts) ? topic.contentTopicPosts : []
      const postsById = new Map()
      existingPosts.forEach((post) => {
        if (post?.id != null) postsById.set(String(post.id), post)
      })
      nextPosts.forEach((post) => {
        if (post?.id != null) postsById.set(String(post.id), post)
      })
      byId.set(sid, {
        ...existing,
        ...topic,
        contentTopicPosts: postsById.size > 0 ? [...postsById.values()] : nextPosts.length ? nextPosts : existingPosts,
      })
    })
  })
  return [...byId.values()]
}

function applyProcessedTopicsFromList(state, data) {
  const processedTopics = {
    drafts: [],
    sent_to_creator: [],
    pending: [],
    scheduled: [],
    live: [],
  }
  const statusCounts = {
    drafts: 0,
    sent_to_creator: 0,
    pending: 0,
    scheduled: 0,
    live: 0,
  }
  const topicCategoryMap = new Map()

  data.forEach((topic) => {
    if (!topic.contentTopicPosts || topic.contentTopicPosts.length === 0) return
    const postStatuses = topic.contentTopicPosts.map((post) => getPostStatusId(post))
    const categoriesForTopic = new Set()
    postStatuses.forEach((statusId) => {
      if (statusId === 1) categoriesForTopic.add("drafts")
      else if (statusId === 4 || statusId === 8) categoriesForTopic.add("pending")
      else if (statusId === 5) categoriesForTopic.add("sent_to_creator")
      else if (statusId === 6) categoriesForTopic.add("scheduled")
      else if (statusId === 7) categoriesForTopic.add("live")
    })

    categoriesForTopic.forEach((categoryKey) => {
      const mapKey = `${topic.id}-${categoryKey}`
      if (topicCategoryMap.has(mapKey) || !(categoryKey in processedTopics)) return
      topicCategoryMap.set(mapKey, true)

      const categoryStatusIds = {
        drafts: [CONTENT_POST_STATUS.DRAFT],
        pending: [CONTENT_POST_STATUS.SENT_FOR_APPROVAL, CONTENT_POST_STATUS.SENT_FOR_EXTERNAL_APPROVAL],
        sent_to_creator: [CONTENT_POST_STATUS.SENT_TO_CREATOR],
        scheduled: [CONTENT_POST_STATUS.SCHEDULED],
        live: [CONTENT_POST_STATUS.POSTED],
      }[categoryKey] || []

      const filteredPosts = topic.contentTopicPosts.filter((post) =>
        categoryStatusIds.includes(getPostStatusId(post)),
      )
      if (filteredPosts.length === 0) return

      const firstPost = filteredPosts[0]
      processedTopics[categoryKey].push({
        id: topic.id,
        name: topic.name,
        campaignName: topic.name || topic.campaignName,
        text: firstPost.postText || firstPost.text || "",
        mediaUrl:
          firstPost.contentTopicPostMedias?.[0]?.mediaAsset?.assetUrl ||
          firstPost.contentTopicPostMedias?.[0]?.media?.mediaAssets?.[0]?.assetUrl ||
          null,
        socialNetworkType:
          firstPost.channel?.socialNetworkType || firstPost.socialNetworkType?.description || null,
        socialNetworkName: firstPost.channel?.name || "",
        event: topic.event
          ? {
              id: topic.event.id || topic.event.eventId || "",
              name: topic.event.name || "",
              category: topic.event.category || "",
              url: topic.event.url || "",
              date: topic.event.date || "",
            }
          : null,
        pillar: topic.contentPillar?.description || "",
        status: categoryKey,
        contentTopicPosts: filteredPosts,
        postDatetime: firstPost.postDatetime,
        brandToneScore: firstPost.brandToneScore || 0,
        firstPost,
        originalTopic: topic,
      })
      statusCounts[categoryKey]++
    })
  })

  state.processedTopics = processedTopics
  state.statusCounts = statusCounts
  state.topics = data
}

export const fetchTopicsWithStatus = createAsyncThunk(
  "topics/fetchWithStatus",
  async (
    { clientId, postStatusIds, date, isYearlyCall = false, startDate, endDate, force = false },
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState()
      const token = state.auth?.token

      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }

      if (!clientId) {
        throw new Error("Client ID is required")
      }

      let start_date, end_date
      let cacheKey

      if (startDate && endDate) {
        start_date = startDate
        end_date = endDate
        cacheKey = `${String(clientId)}|${startDate}|${endDate}`
      } else {
        const targetDate = date || new Date()
        const dates = isYearlyCall ? getYearDates(targetDate) : getMonthDates(targetDate)
        start_date = dates.start_date
        end_date = dates.end_date
        cacheKey = isYearlyCall
          ? `${String(clientId)}|${targetDate.getFullYear()}`
          : topicsMonthCacheKey(clientId, targetDate)
      }

      if (!force && state.topics.fetchedMonths?.[cacheKey]) {
        return {
          data: state.topics.topicsByMonth?.[cacheKey] || [],
          postStatusIds,
          fromCache: true,
          cacheKey,
          clientId,
        }
      }

      const queryParams = new URLSearchParams()
      postStatusIds?.forEach((id) => queryParams.append("post_status_ids", id))
      queryParams.append("start_date", start_date)
      queryParams.append("end_date", end_date)

      const data = await get(`topics/clients/${clientId}?${queryParams.toString()}`)

      return { data, postStatusIds, fromCache: false, cacheKey, clientId }
    } catch (error) {
      console.error("Fetch topics with status error:", error)
      return rejectWithValue(error?.message || "Failed to fetch topics with status")
    }
  },
  {
    condition: (arg, { getState }) => {
      if (arg?.force) return true
      if (!arg?.clientId) return false
      const key = topicsFetchCacheKey(arg)
      const slice = getState().topics ?? {}
      if (slice.fetchedMonths?.[key]) return false
      if (slice.inFlightMonths?.[key]) return false
      return true
    },
  },
)

const initialState = {
  topics: [],
  processedTopics: {
    drafts: [],
    pending: [],
    sent_to_creator: [],
    scheduled: [],
    live: [],
  },
  statusCounts: {
    drafts: 0,
    pending: 0,
    sent_to_creator: 0,
    scheduled: 0,
    live: 0,
  },
  status: "idle", // idle | loading | succeeded | failed
  error: null,
  lastFetchedMonth: null, // Track which month data was fetched for
  topicDetails: null, // Store analytics data
  topicsByMonth: {},
  fetchedMonths: {},
  inFlightMonths: {},
}

const topicsSlice = createSlice({
  name: "topics",
  initialState,
  reducers: {
    clearTopics: (state) => {
      state.topics = []
      state.processedTopics = {
        drafts: [],
        pending: [],
        sent_to_creator: [],
        scheduled: [],
        live: [],
      }
      state.statusCounts = {
        drafts: 0,
        pending: 0,
        sent_to_creator: 0,
        scheduled: 0,
        live: 0,
      }
      state.status = "idle"
      state.error = null
      state.lastFetchedMonth = null
      state.topicsByMonth = {}
      state.fetchedMonths = {}
      state.inFlightMonths = {}
    },
    hydrateTopicsForClient: (state, action) => {
      const clientId = action.payload
      const merged = flattenTopicsForClient(state.topicsByMonth, clientId)
      applyProcessedTopicsFromList(state, merged)
    },
    invalidateTopicsMonth: (state, action) => {
      const { clientId, date } = action.payload || {}
      if (clientId == null) return
      const key = topicsMonthCacheKey(clientId, date)
      if (state.fetchedMonths) delete state.fetchedMonths[key]
      if (state.topicsByMonth) delete state.topicsByMonth[key]
      if (state.inFlightMonths) delete state.inFlightMonths[key]
      const merged = flattenTopicsForClient(state.topicsByMonth, clientId)
      applyProcessedTopicsFromList(state, merged)
    },
    clearTopicsError: (state) => {
      state.error = null
    },
    resetTopicsOnLogout: (state) => {
      return initialState
    },
    checkAndRefreshForNewMonth: (state, action) => {
      const targetDate = action.payload?.date || new Date()
      const currentMonthKey = getMonthKey(targetDate)
      if (state.lastFetchedMonth !== currentMonthKey) {
        // Clear data to force refresh for new month
        state.topics = []
        state.processedTopics = {
          drafts: [],
          pending: [],
          sent_to_creator: [],
          scheduled: [],
          live: [],
        }
        state.statusCounts = {
          drafts: 0,
          pending: 0,
          sent_to_creator: 0,
          scheduled: 0,
          live: 0,
        }
        state.lastFetchedMonth = null
      }
    },
    moveTopicsToApproval: (state, action) => {
      const payload = action.payload
      let topicIds = []
      let postIds = []

      // Support both formats: array of topic IDs or object with topicIds and postIds
      if (Array.isArray(payload)) {
        topicIds = payload
      } else if (payload && typeof payload === "object") {
        topicIds = payload.topicIds || []
        postIds = payload.postIds || []
      }

      // Remove posts from drafts (similar to delete functionality)
      const allTabs = ["drafts", "pending", "scheduled", "live"]

      for (const tabType of allTabs) {
        const campaigns = state.processedTopics[tabType]

        for (let i = 0; i < campaigns.length; i++) {
          const campaign = campaigns[i]

          // If specific postIds are provided, only remove those posts
          if (postIds.length > 0 && topicIds.includes(campaign.id)) {
            const updatedPosts = campaign.contentTopicPosts.filter((p) => !postIds.includes(p.id))

            if (updatedPosts.length === 0) {
              // Remove campaign entirely if no posts left
              state.processedTopics[tabType] = campaigns.filter((c) => c.id !== campaign.id)
              state.statusCounts[tabType] = Math.max(0, state.statusCounts[tabType] - 1)
            } else {
              // Update campaign with remaining posts
              const newFirstPost = updatedPosts[0]

              state.processedTopics[tabType] = campaigns.map((c) => {
                if (c.id === campaign.id) {
                  return {
                    ...c,
                    contentTopicPosts: updatedPosts,
                    firstPost: newFirstPost,
                    text: newFirstPost.postText || "",
                    mediaUrl:
                      newFirstPost.contentTopicPostMedias?.[0]?.mediaAsset?.assetUrl ||
                      newFirstPost.contentTopicPostMedias?.[0]?.media?.mediaAssets?.[0]?.assetUrl ||
                      null,
                    socialNetworkType:
                      newFirstPost.channel?.socialNetworkType || newFirstPost.socialNetworkType?.description || null,
                    socialNetworkName: newFirstPost.channel?.name || "",
                    postDatetime: newFirstPost.postDatetime,
                    brandToneScore: newFirstPost.brandToneScore || 0,
                  }
                }
                return c
              })
            }
          } else if (postIds.length === 0 && topicIds.includes(campaign.id)) {
            // If no specific postIds, remove entire campaign
            state.processedTopics[tabType] = campaigns.filter((c) => c.id !== campaign.id)
            state.statusCounts[tabType] = Math.max(0, state.statusCounts[tabType] - 1)
          }
        }
      }
    },
    moveTopicsToScheduled: (state, action) => {
      const payload = action.payload
      let topicIds = []
      let postIds = []

      // Support both formats: array of topic IDs or object with topicIds and postIds
      if (Array.isArray(payload)) {
        topicIds = payload
      } else if (payload && typeof payload === "object") {
        topicIds = payload.topicIds || []
        postIds = payload.postIds || []
      }

      // Remove posts from drafts (similar to delete functionality)
      const allTabs = ["drafts", "pending", "scheduled", "live"]

      for (const tabType of allTabs) {
        const campaigns = state.processedTopics[tabType]

        for (let i = 0; i < campaigns.length; i++) {
          const campaign = campaigns[i]

          // If specific postIds are provided, only remove those posts
          if (postIds.length > 0 && topicIds.includes(campaign.id)) {
            const updatedPosts = campaign.contentTopicPosts.filter((p) => !postIds.includes(p.id))

            if (updatedPosts.length === 0) {
              // Remove campaign entirely if no posts left
              state.processedTopics[tabType] = campaigns.filter((c) => c.id !== campaign.id)
              state.statusCounts[tabType] = Math.max(0, state.statusCounts[tabType] - 1)
            } else {
              // Update campaign with remaining posts
              const newFirstPost = updatedPosts[0]

              state.processedTopics[tabType] = campaigns.map((c) => {
                if (c.id === campaign.id) {
                  return {
                    ...c,
                    contentTopicPosts: updatedPosts,
                    firstPost: newFirstPost,
                    text: newFirstPost.postText || "",
                    mediaUrl:
                      newFirstPost.contentTopicPostMedias?.[0]?.mediaAsset?.assetUrl ||
                      newFirstPost.contentTopicPostMedias?.[0]?.media?.mediaAssets?.[0]?.assetUrl ||
                      null,
                    socialNetworkType:
                      newFirstPost.channel?.socialNetworkType || newFirstPost.socialNetworkType?.description || null,
                    socialNetworkName: newFirstPost.channel?.name || "",
                    postDatetime: newFirstPost.postDatetime,
                    brandToneScore: newFirstPost.brandToneScore || 0,
                  }
                }
                return c
              })
            }
          } else if (postIds.length === 0 && topicIds.includes(campaign.id)) {
            // If no specific postIds, remove entire campaign
            state.processedTopics[tabType] = campaigns.filter((c) => c.id !== campaign.id)
            state.statusCounts[tabType] = Math.max(0, state.statusCounts[tabType] - 1)
          }
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTopicsByClientId.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(fetchTopicsByClientId.fulfilled, (state, action) => {
        state.status = "succeeded"
        const { data, monthKey, fromCache } = action.payload
        state.topics = data
        state.lastFetchedMonth = monthKey

        const processedTopics = {
          drafts: [],
          pending: [],
          scheduled: [],
          live: [],
        }

        const statusCounts = {
          drafts: 0,
          pending: 0,
          scheduled: 0,
          live: 0,
        }

        data.forEach((topic) => {
          if (topic.contentTopicPosts && topic.contentTopicPosts.length > 0) {
            const firstPost = topic.contentTopicPosts[0]
            const statusId = getPostStatusId(firstPost)
            const statusKey = STATUS_MAPPING[statusId]

            if (statusKey && statusKey in processedTopics) {
              const campaign = {
                id: topic.id,
                name: topic.name,
                campaignName: topic.name || topic.campaignName,
                text: firstPost.postText || "",
                mediaUrl:
                  firstPost.contentTopicPostMedias?.[0]?.mediaAsset?.assetUrl ||
                  firstPost.contentTopicPostMedias?.[0]?.media?.mediaAssets?.[0]?.assetUrl ||
                  null,
                socialNetworkType:
                  firstPost.channel?.socialNetworkType || firstPost.socialNetworkType?.description || null, // e.g. "facebook"
                socialNetworkName: firstPost.channel?.name || "",
                event: topic.event
                  ? {
                    id: topic.event.id || topic.event.eventId || "",
                    name: topic.event.name || "",
                    category: topic.event.category || "",
                    url: topic.event.url || "",
                    date: topic.event.date || "",
                  }
                  : null,
                pillar: topic.contentPillar?.description || "",
                status: statusKey,
                contentTopicPosts: topic.contentTopicPosts || [],
                postDatetime: firstPost.postDatetime,
                brandToneScore: firstPost.brandToneScore || 0,
                firstPost,
                originalTopic: topic,
              }

              processedTopics[statusKey].push(campaign)
              statusCounts[statusKey]++
            }
          }
        })

        state.processedTopics = processedTopics
        state.statusCounts = statusCounts
      })
      .addCase(fetchTopicsByClientId.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload
      })
      .addCase(fetchTopicDetails.pending, (state) => {
        state.topicDetails = null
      })
      .addCase(fetchTopicDetails.fulfilled, (state, action) => {
        state.topicDetails = normalizePostAnalyticsResponse(action.payload)
      })
      .addCase(fetchTopicDetails.rejected, (state, action) => {
        state.topicDetails = null
      })
      .addCase(deleteTopic.fulfilled, (state, action) => {
        const { postId } = action.payload

        const allTabs = ["drafts", "pending", "scheduled", "live"]
        let postFound = false

        for (const tabType of allTabs) {
          const campaigns = state.processedTopics[tabType]

          for (let i = 0; i < campaigns.length; i++) {
            const campaign = campaigns[i]
            const postIndex = campaign.contentTopicPosts.findIndex((p) => p.id == postId)

            if (postIndex !== -1) {
              postFound = true

              // Filter out the deleted post
              const updatedPosts = campaign.contentTopicPosts.filter((p) => p.id != postId)

              if (updatedPosts.length === 0) {
                // Remove campaign entirely
                state.processedTopics[tabType] = campaigns.filter((c) => c.id !== campaign.id)
                state.statusCounts[tabType] = Math.max(0, state.statusCounts[tabType] - 1)
              } else {
                // Update campaign with new posts
                const newFirstPost = updatedPosts[0]

                state.processedTopics[tabType] = campaigns.map((c) => {
                  if (c.id === campaign.id) {
                    return {
                      ...c,
                      contentTopicPosts: updatedPosts,
                      firstPost: newFirstPost,
                      text: newFirstPost.postText || "",
                      mediaUrl:
                        newFirstPost.contentTopicPostMedias?.[0]?.mediaAsset?.assetUrl ||
                        newFirstPost.contentTopicPostMedias?.[0]?.media?.mediaAssets?.[0]?.assetUrl ||
                        null,
                      socialNetworkType:
                        newFirstPost.channel?.socialNetworkType || newFirstPost.socialNetworkType?.description || null,
                      socialNetworkName: newFirstPost.channel?.name || "",
                      postDatetime: newFirstPost.postDatetime,
                      brandToneScore: newFirstPost.brandToneScore || 0,
                    }
                  }
                  return c
                })
              }
              return
            }
          }
        }

        if (!postFound) {
          console.error("Post not found in topics slice:", postId)
        }
      })
      .addCase(deletePostsBulk.fulfilled, (state, action) => {
        const postIds = action.payload?.postIds || []
        for (const postId of postIds) {
          const allTabs = ["drafts", "pending", "scheduled", "live", "sent_to_creator"]
          for (const tabType of allTabs) {
            const campaigns = state.processedTopics[tabType]
            if (!Array.isArray(campaigns)) continue

            for (let i = 0; i < campaigns.length; i++) {
              const campaign = campaigns[i]
              const postIndex = campaign.contentTopicPosts?.findIndex((p) => p.id == postId)
              if (postIndex === -1) continue

              const updatedPosts = campaign.contentTopicPosts.filter((p) => p.id != postId)
              if (updatedPosts.length === 0) {
                state.processedTopics[tabType] = campaigns.filter((c) => c.id !== campaign.id)
                state.statusCounts[tabType] = Math.max(0, (state.statusCounts[tabType] || 0) - 1)
              } else {
                const newFirstPost = updatedPosts[0]
                state.processedTopics[tabType] = campaigns.map((c) => {
                  if (c.id !== campaign.id) return c
                  return {
                    ...c,
                    contentTopicPosts: updatedPosts,
                    firstPost: newFirstPost,
                    text: newFirstPost.postText || "",
                    mediaUrl:
                      newFirstPost.contentTopicPostMedias?.[0]?.mediaAsset?.assetUrl ||
                      newFirstPost.contentTopicPostMedias?.[0]?.media?.mediaAssets?.[0]?.assetUrl ||
                      null,
                    socialNetworkType:
                      newFirstPost.channel?.socialNetworkType ||
                      newFirstPost.socialNetworkType?.description ||
                      null,
                    socialNetworkName: newFirstPost.channel?.name || "",
                    postDatetime: newFirstPost.postDatetime,
                    brandToneScore: newFirstPost.brandToneScore || 0,
                  }
                })
              }
              break
            }
          }
        }
      })
      .addCase(fetchTopicsWithStatus.pending, (state, action) => {
        const arg = action.meta?.arg || {}
        const key = topicsFetchCacheKey(arg)
        if (!state.inFlightMonths) state.inFlightMonths = {}
        if (key) state.inFlightMonths[key] = true
        const hasExistingTopics =
          state.status === "succeeded" ||
          (Array.isArray(state.topics) && state.topics.length > 0)
        // Keep the review/calendar feed mounted while a save refreshes in the background.
        if (arg.background && hasExistingTopics) {
          state.error = null
          return
        }
        if (arg.force) {
          state.status = "loading"
          state.error = null
          return
        }
        if (state.fetchedMonths?.[key]) return
        state.status = "loading"
        state.error = null
      })
      .addCase(fetchTopicsWithStatus.fulfilled, (state, action) => {
        state.status = "succeeded"
        const { data, fromCache, cacheKey, clientId } = action.payload || {}
        if (cacheKey && state.inFlightMonths) delete state.inFlightMonths[cacheKey]
        if (!fromCache && cacheKey && Array.isArray(data)) {
          if (!state.topicsByMonth) state.topicsByMonth = {}
          if (!state.fetchedMonths) state.fetchedMonths = {}
          state.topicsByMonth[cacheKey] = data
          state.fetchedMonths[cacheKey] = true
        }
        const merged = flattenTopicsForClient(state.topicsByMonth, clientId)
        applyProcessedTopicsFromList(state, merged)
      })
      .addCase(fetchTopicsWithStatus.rejected, (state, action) => {
        const arg = action.meta?.arg || {}
        const key = topicsFetchCacheKey(arg)
        if (key && state.inFlightMonths) delete state.inFlightMonths[key]
        const keepExistingUi =
          Boolean(arg.background) &&
          (state.status === "succeeded" ||
            (Array.isArray(state.topics) && state.topics.length > 0))
        if (keepExistingUi) return
        state.status = "failed"
        state.error = action.payload
      })
  },
})

export const {
  clearTopics,
  clearTopicsError,
  resetTopicsOnLogout,
  checkAndRefreshForNewMonth,
  moveTopicsToApproval,
  moveTopicsToScheduled,
  hydrateTopicsForClient,
  invalidateTopicsMonth,
} = topicsSlice.actions

/** After create/edit/schedule: refresh that post’s month. Keep on-screen data until the new fetch lands. */
export const refreshTopicsAfterPostMutation = createAsyncThunk(
  "topics/refreshAfterPostMutation",
  async ({ clientId, postStatusIds, postDate, visibleDate }, { dispatch }) => {
    const monthDate = resolveMonthlyTopicsDate(postDate, visibleDate)
    const visible = coerceTopicsMonthDate(visibleDate)
    const postMonth = coerceTopicsMonthDate(monthDate)

    const refreshMonth = async (date) => {
      await dispatch(
        fetchTopicsWithStatus({
          ...monthlyTopicsFetchPayload(clientId, postStatusIds, date, true),
          background: true,
        }),
      ).unwrap()
    }

    if (visible) {
      await refreshMonth(visible)
      if (
        postMonth &&
        topicsMonthCacheKey(clientId, postMonth) !== topicsMonthCacheKey(clientId, visible)
      ) {
        await refreshMonth(postMonth)
      }
    } else {
      await refreshMonth(monthDate)
    }
    return { clientId, monthDate }
  },
)

export default topicsSlice.reducer
